import { useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

interface PopupProps {
  isOpen: boolean;
  title: string;
  description: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
}

export default function Popup({
  isOpen,
  title,
  description,
  onConfirm,
  onCancel,
  confirmText,
  cancelText,
}: PopupProps) {
  const popupRef = useRef<HTMLDivElement>(null);

  // ESC-btn
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onCancel]);

  // handle click outside popup
  const handleClickOutside = (e: React.MouseEvent<HTMLDivElement>) => {
    if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
      onCancel();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: "rgba(56, 56, 56, 0.8)" }}
      onClick={handleClickOutside}
    >
      <div
        ref={popupRef}
        className="bg-white max-w-[500px] w-full mx-4 p-4 md:p-6 md:pb-8 relative"
      >
        <button   
          className="flex text-black mb-6 ml-auto gap-2 items-center
          focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus focus-visible:outline-offset-4
          active:outline-none
          md:hover:underline md:hover:decoration-1   
          focus-visible:underline focus-visible:decoration-1 
          md:active:underline md:active:decoration-2
          active:bg-lightgray"

          onClick={onCancel}
        >
            <FontAwesomeIcon className="text-xl" icon={solid("xmark")} />
            <span className="text-lg">Stäng</span>
        </button>

        <h1 className="mb-2">{title}</h1>
        <p>{description}</p>

        <div className="flex flex-col gap-4">
          <button
            className="btn secondary"
            onClick={onCancel}
          >
            {cancelText || "Avbryt"}
          </button>
          <button
            className="btn primary"
            onClick={onConfirm}
          >
            {confirmText || "Bekräfta"}
          </button>
        </div>
      </div>
    </div>
  );
}